import React, { useState } from 'react';
import { Box, Typography, Divider, Chip } from '@mui/material';
import CustomDrawer from '../../../../../../shared/CustomDrawer/CustomDrawer';
import DeleteConfirmationModal from '../DeleteConfirmationModal';
import CustomAlert from '../../../../../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../../../../../shared/CustomAlert/CustomAlertContain';
import { AdminService } from '../../../../../../../service/admin.service';
import zeplinColor from '../../../../../../../theme/Color';
import style from './UserDetailsDrawer.module.css';

interface UserDetailsDrawerProps {
  open: boolean;
  user: {
    id?: string;
    name?: string | null;
    username?: string | null;
    role?: { roleId: string; roleName: string } | null;
    email: string | null;
    contactedServices?: Record<string, boolean> | boolean | null;
    mobileNumber?: string | null;
    lastLogin?: string | null;
    status?: 'active' | 'blocked';
    profileImage?: string | null;
  } | null;
  onClose: () => void;
  refreshUsers?: () => Promise<void>;
  onDeleteSuccess?: () => void;
  onEditClick?: () => void;
}

const UserDetailsDrawer: React.FC<UserDetailsDrawerProps> = ({
  open,
  user,
  onClose,
  refreshUsers,
  onDeleteSuccess,
  onEditClick,
}) => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertError, setAlertError] = useState(false);
  const handleClose = (isOpen: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    onClose();
  };

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };

  const showToast = (message: string, isError = false) => {
    setAlertMessage(message);
    setAlertError(isError);
    setAlertOpen(true);
  };

  const handleAlertClose = () => {
    setAlertOpen(false);
  };

  const handleDeleteClick = () => {
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!user?.email) return;
    try {
      setLoading(true);
      await AdminService.deleteUser(user.email);
      if (refreshUsers) {
        await refreshUsers();
      }
      setDeleteModalOpen(false);
      onClose();
      if (onDeleteSuccess) {
        onDeleteSuccess();
      }
    } catch (error) {
      showToast('Failed to delete user.', true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteModalOpen(false);
  };

  return (
    <CustomDrawer state={open} onClose={handleClose(false)}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'auto' }}>
        {/* Header */}
        <Box
          sx={{ position: 'sticky', top: 0, backgroundColor: zeplinColor.Neutral1, zIndex: '1' }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: { xs: '30px 20px 20px 20px', xl: '40px 24px 24px 24px' },
            }}
          >
            <Typography variant='responsiveHeading2' color={zeplinColor.BasicBlack}>
              User Details
            </Typography>
            <img
              src={'/assets/close-icon.svg'}
              alt='close'
              style={{ cursor: 'pointer', width: '24px', height: '24px' }}
              onClick={handleClose(false)}
            />
          </Box>
          <Divider component='div' sx={{ borderColor: zeplinColor.Neutral5 }} />
        </Box>

        {/* Content */}
        <Box sx={{ padding: { xs: '20px', xl: '24px' } }}>
          {user ? (
            <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, flexDirection: 'column' }}>
              {/* User Profile Section */}
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: '12px', xl: '16px' },
                    alignItems: 'flex-start',
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: '50px', xl: '60px' },
                      height: { xs: '50px', xl: '60px' },
                      borderRadius: '14px',
                      backgroundColor: user?.profileImage ? 'transparent' : zeplinColor.Brand1,
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      overflow: 'hidden',
                      flexShrink: 0,
                    }}
                  >
                    {user?.profileImage ? (
                      <img
                        src={user.profileImage}
                        alt='profile'
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          borderRadius: '12px',
                        }}
                      />
                    ) : (
                      <Typography
                        variant='responsiveHeading3'
                        color={zeplinColor.Neutral1}
                        sx={{ fontWeight: 600 }}
                      >
                        {getInitials(user?.name)}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      flex: 1,
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                      {user?.name || 'N/A'}
                    </Typography>
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral8}>
                      {user?.email || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { xs: '10px', xl: '16px' },
                    alignItems: 'center',
                    // justifyContent: 'space-between',
                    // padding: '40px 24px 24px 24px',
                  }}
                >
                  <img
                    src={'/assets/edit.svg'}
                    className={style['icon']}
                    alt='edit'
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (onEditClick) {
                        onEditClick();
                        onClose();
                      }
                    }}
                  />

                  <img
                    src={'/assets/delete-red.png'}
                    className={style['icon']}
                    alt='delete'
                    style={{ cursor: 'pointer' }}
                    onClick={handleDeleteClick}
                  />
                </Box>
              </Box>

              <Divider component='div' sx={{ borderColor: zeplinColor.Neutral5 }} />

              {/* Details Section */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
                  Personal Details
                </Typography>
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='responsiveBody2'
                    color={zeplinColor.Neutral8}
                    sx={{ marginBottom: '8px', minWidth: '170px' }}
                  >
                    Name
                  </Typography>
                  <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                    {user?.name || 'N/A'}
                  </Typography>
                </Box>

                {/* Role */}
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='responsiveBody2'
                    color={zeplinColor.Neutral8}
                    sx={{ marginBottom: '8px', minWidth: '170px' }}
                  >
                    Role
                  </Typography>
                  <Chip
                    label={
                      <Typography
                        variant='responsiveLabel1'
                        color={zeplinColor.Neutral1}
                        sx={{
                          textAlign: 'center',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        {user?.role?.roleName || 'N/A'}
                      </Typography>
                    }
                    sx={{
                      backgroundColor: zeplinColor.BasicBlack,
                      padding: '5px 12px',
                      borderRadius: '50px',
                      height: '24px',
                    }}
                  />
                </Box>

                {/* Email */}
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='responsiveBody2'
                    color={zeplinColor.Neutral8}
                    sx={{ marginBottom: '8px', minWidth: '170px' }}
                  >
                    Email
                  </Typography>
                  <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                    {user?.email || 'N/A'}
                  </Typography>
                </Box>

                {/* Contacted Services */}
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='responsiveBody2'
                    color={zeplinColor.Neutral8}
                    sx={{ marginBottom: '8px', minWidth: '170px' }}
                  >
                    Contacted Services
                  </Typography>
                  {(() => {
                    const services =
                      typeof user?.contactedServices === 'object' &&
                      user?.contactedServices !== null
                        ? (user.contactedServices as Record<string, boolean>)
                        : null;
                    const serviceIconMap: Record<string, string> = {
                      salesforce: '/assets/salesforce.svg',
                      slack: '/assets/slack.svg',
                      googleCalendar: '/assets/google-calendar.svg',
                      msTeams: '/assets/ms-teams.svg',
                      zoom: '/assets/zoom.svg',
                    };
                    const hasServices = services && Object.values(services).some((v) => v === true);
                    const connectedServices = services
                      ? Object.entries(services)
                          .filter(([_, isConnected]) => isConnected)
                          .map(([serviceName]) => ({
                            name: serviceName,
                            displayName: serviceName
                              .replace(/([A-Z])/g, ' $1')
                              .replace(/^./, (str) => str.toUpperCase())
                              .trim(),
                            icon: serviceIconMap[serviceName] || '/assets/information-circle.svg',
                          }))
                      : [];

                    if (connectedServices.length > 0) {
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                          }}
                        >
                          {connectedServices.map((service) => (
                            <Box
                              key={service.name}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                              }}
                            >
                              <img
                                src={service.icon}
                                alt={service.displayName}
                                width='18px'
                                height='18px'
                                style={{ objectFit: 'contain' }}
                              />
                              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                                {service.displayName}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    }

                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                        }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: zeplinColor.AlertInfo,
                          }}
                        />
                        <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                          No
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>

                {/* Mobile Number */}
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='responsiveBody2'
                    color={zeplinColor.Neutral8}
                    sx={{ marginBottom: '8px', minWidth: '170px' }}
                  >
                    Mobile Number
                  </Typography>
                  <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                    {user?.mobileNumber || 'N/A'}
                  </Typography>
                </Box>

                {/* Last Login */}
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='responsiveBody2'
                    color={zeplinColor.Neutral8}
                    sx={{ marginBottom: '8px', minWidth: '170px' }}
                  >
                    Last Login
                  </Typography>
                  <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                    {user?.lastLogin || 'N/A'}
                  </Typography>
                </Box>
                {/* Status */}
                <Box sx={{ display: 'flex' }}>
                  <Typography
                    variant='responsiveBody2'
                    color={zeplinColor.Neutral8}
                    sx={{ marginBottom: '8px', minWidth: '170px' }}
                  >
                    Status
                  </Typography>
                  <Typography variant='responsiveBody2' color={zeplinColor.BasicBlack}>
                    {user?.status === 'active' ? 'Active' : 'Blocked'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <Typography variant='responsiveBody2' color={zeplinColor.Neutral8}>
              No user selected
            </Typography>
          )}
        </Box>
      </Box>
      <DeleteConfirmationModal
        open={deleteModalOpen}
        userName={user?.name || ''}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={loading}
      />
      <CustomAlert
        open={alertOpen}
        handleClose={handleAlertClose}
        message={
          <CustomAlertContain
            alertIcon={
              alertError ? (
                <img src='/assets/error.svg' width={24} height={24} />
              ) : (
                <img src='/assets/checkmark-green.png' width={24} height={24} />
              )
            }
            alert={alertMessage}
            error={alertError}
          />
        }
        error={alertError}
      />
    </CustomDrawer>
  );
};

export default UserDetailsDrawer;
